/**
 * biome-textures.ts — procedural per-biome texture generation
 * (v2M7.7b/c).
 *
 * Each `BiomeKind` gets a 128×128 OffscreenCanvas painted at app-boot
 * (lazily, on first request) with biome-specific procedural noise. The
 * canvas is then blitted at 40 % alpha clipped to each planet disc by
 * `system-view.ts`, layered over the §5.3 radial gradient. This is the
 * "procedural-not-AI" path locked in GRAPHICS §10.2 (2026-04-26): the
 * runtime contract — load 128×128 image, blit at 40 % alpha — is the
 * same one the v3M11 polish pass will use when commissioned PNG art
 * lands.
 *
 * Design ground rules:
 *   - **Deterministic.** Each biome seeds its own Mulberry32 PRNG from
 *     a hash of the biome name; the same biome generates pixel-identical
 *     textures every session. Useful when QA captures a screenshot in
 *     one run and diffs against another.
 *   - **Once-per-session cached.** First call paints, subsequent calls
 *     return the cached canvas. ~7 ms per biome on a modern laptop.
 *   - **Headless-safe.** `OffscreenCanvas` is undefined in the Node
 *     test environment (vitest.config.ts environment='node'). The
 *     accessor returns `null` in that case, and `system-view.ts`
 *     skips the blit. No tests break, no DOM stub needed.
 *
 * Per-biome motifs implement the §5.3 spec column:
 *   - barren      → cratered rock (scattered dark circles + rim hi-lights)
 *   - temperate   → continent + reef (large green blobs, lighter halos)
 *   - desert      → dune shadows (angled sine-modulated bands)
 *   - arctic      → ice crack pattern (recursive branching white lines)
 *   - ocean       → wave cap speckle (small bright dots + faint streaks)
 *   - volcanic    → fissure glow veins (recursive orange branches +
 *                   radial lava-pool glows)
 *   - gaia        → swirled cloud + ocean (large soft blobs alternating
 *                   cloud-white and ocean-green tints)
 *
 * Spec biomes Toxic + Radioactive aren't in the v1 BiomeKind enum;
 * if `PLANETS.md` later adds them, drop a generator into the switch.
 */

import type { BiomeKind } from '../state/types';

const TEXTURE_SIZE = 128;

/**
 * Cache keyed on biome — value `null` when OffscreenCanvas isn't
 * supported (test env), so subsequent calls also short-circuit cheaply.
 */
const cache = new Map<BiomeKind, OffscreenCanvas | null>();

/**
 * Mulberry32 — small, fast, decent-quality 32-bit PRNG. Seeded from
 * the biome name so re-runs produce identical textures.
 */
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 0x100000000;
  };
}

/** FNV-1a hash of the biome name → stable 32-bit seed. */
function biomeSeed(biome: BiomeKind): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < biome.length; i++) {
    h ^= biome.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h;
}

/**
 * Get (or generate-and-cache) the procedural texture for a biome.
 * Returns `null` when OffscreenCanvas is unavailable (node test env).
 */
export function getBiomeTexture(biome: BiomeKind): OffscreenCanvas | null {
  if (cache.has(biome)) return cache.get(biome) ?? null;
  if (typeof OffscreenCanvas === 'undefined') {
    cache.set(biome, null);
    return null;
  }
  const canvas = new OffscreenCanvas(TEXTURE_SIZE, TEXTURE_SIZE);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    cache.set(biome, null);
    return null;
  }
  const rng = mulberry32(biomeSeed(biome));
  switch (biome) {
    case 'barren':
      drawBarrenCraters(ctx, rng);
      break;
    case 'temperate':
      drawTropicalContinents(ctx, rng);
      break;
    case 'desert':
      drawDesertDunes(ctx, rng);
      break;
    case 'arctic':
      drawFrozenCracks(ctx, rng);
      break;
    case 'ocean':
      drawOceanicSpeckle(ctx, rng);
      break;
    case 'volcanic':
      drawVolcanicVeins(ctx, rng);
      break;
    case 'gaia':
      drawGaiaSwirls(ctx, rng);
      break;
  }
  cache.set(biome, canvas);
  return canvas;
}

/**
 * v4M1.8a — load a commissioned biome texture and replace the
 * procedural cache entry for the given biome. Promise always resolves
 * (a 404 / decode failure leaves the procedural canvas in place).
 * Painted into a 128×128 OffscreenCanvas regardless of source
 * dimensions; commissioned biome textures should be authored at
 * exactly 128×128 to avoid scaling. Headless-safe.
 */
export function loadBiomeTextureImage(biome: BiomeKind, url: string): Promise<void> {
  return new Promise((resolve) => {
    if (typeof OffscreenCanvas === 'undefined' || typeof Image === 'undefined') {
      resolve();
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = new OffscreenCanvas(TEXTURE_SIZE, TEXTURE_SIZE);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve();
          return;
        }
        ctx.drawImage(img, 0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
        cache.set(biome, canvas);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn(`biome-textures.loadBiomeTextureImage(${biome}) paint failed: ${msg}`);
      }
      resolve();
    };
    img.onerror = () => {
      // Silent on 404 / decode failure — procedural fallback stays
      // active. main.ts attempts all 7 biomes at boot regardless of
      // which commissioned files exist.
      resolve();
    };
    img.src = url;
  });
}

// ── Per-biome generators ─────────────────────────────────────────────

/**
 * Barren — scattered crater pits with sun-side rim highlights for
 * fake 3D pop. ~50 craters of varying size; each one paints a dark
 * circle then a small bright rim on its upper-left to suggest a
 * raised lip catching light from the upper-left.
 */
function drawBarrenCraters(ctx: OffscreenCanvasRenderingContext2D, rng: () => number): void {
  // Faint global wash so the gradient underneath reads slightly darker.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.05)';
  ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
  for (let i = 0; i < 50; i++) {
    const cx = rng() * TEXTURE_SIZE;
    const cy = rng() * TEXTURE_SIZE;
    const r = 1 + rng() * 6;
    ctx.fillStyle = `rgba(30, 25, 18, ${(0.35 + rng() * 0.35).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(230, 220, 200, ${(0.25 + rng() * 0.25).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(cx - r * 0.4, cy - r * 0.4, r * 0.35, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Tropical (mapped from BiomeKind 'temperate') — large irregular
 * green continent blobs with lighter "reef" halos at the edges. ~8
 * landmasses of varying size; reef glow drawn first as a slightly
 * larger lighter circle, then continent overlaid in a darker green.
 */
function drawTropicalContinents(
  ctx: OffscreenCanvasRenderingContext2D,
  rng: () => number,
): void {
  for (let i = 0; i < 8; i++) {
    const cx = rng() * TEXTURE_SIZE;
    const cy = rng() * TEXTURE_SIZE;
    const r = 8 + rng() * 24;
    ctx.fillStyle = `rgba(160, 240, 180, ${(0.3 + rng() * 0.2).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 1.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = `rgba(40, 110, 60, ${(0.5 + rng() * 0.3).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * Desert — angled sine-modulated bands suggesting parallel dune
 * ridges with shadowed troughs. Wave amplitude jittered per-pixel
 * to break up the perfect-sinusoid feel. 2×2 cell painting (so 64×64
 * effective work) keeps generation under a few ms.
 */
function drawDesertDunes(ctx: OffscreenCanvasRenderingContext2D, rng: () => number): void {
  const angle = Math.PI / 5;
  const cosA = Math.cos(angle);
  const sinA = Math.sin(angle);
  // Pre-build a jitter table so the per-pixel call is just a lookup.
  const jitter = new Float32Array(256);
  for (let i = 0; i < 256; i++) jitter[i] = rng() * 4 - 2;
  for (let y = 0; y < TEXTURE_SIZE; y += 2) {
    for (let x = 0; x < TEXTURE_SIZE; x += 2) {
      const u = x * cosA + y * sinA;
      const j = jitter[Math.floor(u + TEXTURE_SIZE) & 0xff];
      const wave = Math.sin((u + j) * 0.18);
      const intensity = Math.abs(wave);
      ctx.fillStyle =
        wave > 0
          ? `rgba(240, 210, 150, ${(0.1 + intensity * 0.25).toFixed(3)})`
          : `rgba(60, 35, 10, ${(0.1 + intensity * 0.3).toFixed(3)})`;
      ctx.fillRect(x, y, 2, 2);
    }
  }
}

/**
 * Frozen (mapped from BiomeKind 'arctic') — sparse branching crack
 * pattern in icy white over a faint cool haze. Recursive: each root
 * spawns up to 3 levels of sub-branches with random bend per segment.
 */
function drawFrozenCracks(ctx: OffscreenCanvasRenderingContext2D, rng: () => number): void {
  ctx.fillStyle = 'rgba(220, 235, 250, 0.08)';
  ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
  ctx.strokeStyle = 'rgba(230, 240, 250, 0.7)';
  ctx.lineWidth = 0.8;
  for (let i = 0; i < 6; i++) {
    drawCrackBranch(ctx, rng() * TEXTURE_SIZE, rng() * TEXTURE_SIZE, rng() * Math.PI * 2, 18 + rng() * 12, 3, rng);
  }
}

function drawCrackBranch(
  ctx: OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  length: number,
  depth: number,
  rng: () => number,
): void {
  if (depth === 0 || length < 2) return;
  const segCount = 4;
  let cx = x;
  let cy = y;
  let a = angle;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  for (let s = 0; s < segCount; s++) {
    a += (rng() - 0.5) * 0.6;
    cx += Math.cos(a) * (length / segCount);
    cy += Math.sin(a) * (length / segCount);
    ctx.lineTo(cx, cy);
  }
  ctx.stroke();
  if (depth > 1 && rng() > 0.3) {
    drawCrackBranch(ctx, cx, cy, a + (rng() - 0.5) * 1.5, length * 0.6, depth - 1, rng);
  }
}

/**
 * Oceanic (mapped from BiomeKind 'ocean') — small white-blue speckles
 * (wave caps) plus a few near-horizontal faint streaks (current lines).
 * Lots of small features keeps the texture reading as "ocean surface"
 * even at small disc sizes.
 */
function drawOceanicSpeckle(
  ctx: OffscreenCanvasRenderingContext2D,
  rng: () => number,
): void {
  for (let i = 0; i < 120; i++) {
    const x = rng() * TEXTURE_SIZE;
    const y = rng() * TEXTURE_SIZE;
    const r = 0.5 + rng() * 1.2;
    ctx.fillStyle = `rgba(220, 240, 255, ${(0.4 + rng() * 0.4).toFixed(3)})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.strokeStyle = 'rgba(180, 220, 255, 0.15)';
  ctx.lineWidth = 0.6;
  for (let i = 0; i < 12; i++) {
    const y = rng() * TEXTURE_SIZE;
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(TEXTURE_SIZE, y + (rng() - 0.5) * 6);
    ctx.stroke();
  }
}

/**
 * Volcanic — dark base with bright orange branching fissures and a
 * handful of radial lava-pool glow blobs. Veins use the same
 * recursive-branch approach as Frozen but with thicker bright strokes
 * and a hotter palette.
 */
function drawVolcanicVeins(
  ctx: OffscreenCanvasRenderingContext2D,
  rng: () => number,
): void {
  ctx.fillStyle = 'rgba(30, 8, 4, 0.3)';
  ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);
  ctx.strokeStyle = 'rgba(255, 130, 50, 0.85)';
  ctx.lineWidth = 1.2;
  for (let i = 0; i < 8; i++) {
    drawVeinBranch(
      ctx,
      rng() * TEXTURE_SIZE,
      rng() * TEXTURE_SIZE,
      rng() * Math.PI * 2,
      30 + rng() * 20,
      3,
      rng,
    );
  }
  for (let i = 0; i < 6; i++) {
    const x = rng() * TEXTURE_SIZE;
    const y = rng() * TEXTURE_SIZE;
    const r = 2 + rng() * 4;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r * 2);
    g.addColorStop(0, 'rgba(255, 220, 80, 0.7)');
    g.addColorStop(1, 'rgba(255, 130, 50, 0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r * 2, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawVeinBranch(
  ctx: OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  angle: number,
  length: number,
  depth: number,
  rng: () => number,
): void {
  if (depth === 0 || length < 2) return;
  const segCount = 5;
  let cx = x;
  let cy = y;
  let a = angle;
  ctx.beginPath();
  ctx.moveTo(cx, cy);
  for (let s = 0; s < segCount; s++) {
    a += (rng() - 0.5) * 0.5;
    cx += Math.cos(a) * (length / segCount);
    cy += Math.sin(a) * (length / segCount);
    ctx.lineTo(cx, cy);
  }
  ctx.stroke();
  if (depth > 1 && rng() > 0.4) {
    drawVeinBranch(ctx, cx, cy, a + (rng() - 0.5) * 1.4, length * 0.6, depth - 1, rng);
  }
}

/**
 * Gaia — large soft radial blobs alternating between cloud-white and
 * ocean-green. ~20 blobs at varying sizes means there's always
 * something to look at no matter where the texture is sampled.
 */
function drawGaiaSwirls(ctx: OffscreenCanvasRenderingContext2D, rng: () => number): void {
  for (let i = 0; i < 20; i++) {
    const x = rng() * TEXTURE_SIZE;
    const y = rng() * TEXTURE_SIZE;
    const r = 8 + rng() * 16;
    const isCloud = rng() > 0.4;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    if (isCloud) {
      g.addColorStop(0, 'rgba(220, 250, 230, 0.6)');
      g.addColorStop(1, 'rgba(220, 250, 230, 0)');
    } else {
      g.addColorStop(0, 'rgba(40, 130, 90, 0.5)');
      g.addColorStop(1, 'rgba(40, 130, 90, 0)');
    }
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}
